/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import 'mapbox-gl/dist/mapbox-gl.css'
import MapboxMap, { type MapMouseEvent, type MapRef, NavigationControl, Popup } from 'react-map-gl/mapbox'

import { type MapStyle, MapStyleToggles, mapStyleUrl } from '@/lib/map/MapStyleToggles'

import { GEOFENCE_FILL_LAYER, GeofenceLayers } from './components/GeofenceLayers'
import { boundsOf, ringFromCoordinates } from './geometry'
import type { GeofenceBounds, GeofenceMapViewport, ManagedGeofence } from './types'

const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN

export const WORLD_VIEW = { longitude: 20, latitude: 15, zoom: 1.2 }
const FIT_OPTIONS = { padding: 60, maxZoom: 16, duration: 600 }

const toLngLatBounds = (b: GeofenceBounds): [[number, number], [number, number]] => [
  [b.west, b.south],
  [b.east, b.north],
]

/** The box around every given geofence, from their outlines. */
export const extentOf = (geofences: ManagedGeofence[]): GeofenceBounds | null =>
  boundsOf(geofences.flatMap((g) => ringFromCoordinates(g.coordinates)))

interface GeofenceOverviewMapProps {
  /** Changes when the company does: the current visible region is reported again. */
  companyId: number | undefined
  geofences: ManagedGeofence[]
  selectedId?: number
  hoveredId?: number
  onSelect: (id: number) => void
  limitToMap?: boolean
  onViewportChange?: (viewport: GeofenceMapViewport) => void
}

/**
 * Every listed geofence on one map (Refresh showed only the open one), so overlaps and gaps are
 * visible at a glance. Starts at the world view and preserves the user's current camera while
 * Limit to map refreshes the polygons. Clicking a geofence opens it, as clicking its row does.
 */
export const GeofenceOverviewMap = ({
  companyId,
  geofences,
  selectedId,
  hoveredId,
  onSelect,
  limitToMap = false,
  onViewportChange,
}: GeofenceOverviewMapProps) => {
  const mapRef = useRef<MapRef>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [style, setStyle] = useState<MapStyle>('road')
  const [mapReady, setMapReady] = useState(false)
  const [hover, setHover] = useState<{ id: number; name: string; longitude: number; latitude: number }>()

  const selected = useMemo(() => geofences.find((geofence) => geofence.id === selectedId), [geofences, selectedId])

  const reportViewport = useCallback(() => {
    if (!limitToMap || !mapReady || !onViewportChange) return
    const map = mapRef.current?.getMap()
    if (!map) return
    const bounds = map.getBounds()
    if (!bounds) return
    const center = map.getCenter()
    const earthCircumferenceMetres = 40075016.686
    const metersPerPixel =
      (Math.cos((center.lat * Math.PI) / 180) * earthCircumferenceMetres) / (512 * Math.pow(2, map.getZoom()))
    onViewportChange({
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
      metersPerPixel,
    })
  }, [limitToMap, mapReady, onViewportChange])

  useEffect(() => {
    reportViewport()
  }, [companyId, reportViewport])

  // The detail pane changes the map width. Resize Mapbox so the current
  // viewport remains accurate for the next LocationMap-compatible boundary request.
  useEffect(() => {
    const container = mapContainerRef.current
    if (!mapReady || !container || typeof ResizeObserver === 'undefined') return

    let frame = 0
    const resizeAndReport = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const map = mapRef.current?.getMap()
        if (!map) return
        map.resize()
        if (!limitToMap && selected) {
          const bounds = selected.bounds ?? boundsOf(ringFromCoordinates(selected.coordinates))
          if (bounds) map.fitBounds(toLngLatBounds(bounds), { ...FIT_OPTIONS, duration: 0 })
        }
        reportViewport()
      })
    }

    const observer = new ResizeObserver(resizeAndReport)
    observer.observe(container)
    resizeAndReport()
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [limitToMap, mapReady, reportViewport, selected])

  // Regular geofence view focuses the selected polygon. Limit to map deliberately preserves
  // the current region while the boundary query updates the visible polygons.
  useEffect(() => {
    const map = mapRef.current
    if (!mapReady || !map || limitToMap || !selected) return
    const bounds = selected.bounds ?? boundsOf(ringFromCoordinates(selected.coordinates))
    if (bounds) map.fitBounds(toLngLatBounds(bounds), FIT_OPTIONS)
  }, [limitToMap, mapReady, selected])

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const id = event.features?.[0]?.properties?.id as number | undefined
      if (id !== undefined) onSelect(Number(id))
    },
    [onSelect]
  )

  const handleMouseMove = useCallback((event: MapMouseEvent) => {
    const feature = event.features?.[0]
    if (!feature?.properties) {
      setHover(undefined)
      return
    }
    setHover({
      id: Number(feature.properties.id),
      name: String(feature.properties.name),
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    })
  }, [])

  const highlightIds = [selectedId, hoveredId, hover?.id].filter((id): id is number => id !== undefined)

  return (
    <div ref={mapContainerRef} className="relative h-full w-full" data-testid="geofence-overview-map">
      <MapboxMap
        // Remount on a style change, as the other Mapbox maps do; refit once it has loaded again.
        key={`${style}-${companyId ?? 'none'}`}
        ref={mapRef}
        mapboxAccessToken={mapboxToken}
        mapStyle={mapStyleUrl(style)}
        initialViewState={WORLD_VIEW}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={[GEOFENCE_FILL_LAYER]}
        cursor={hover ? 'pointer' : 'grab'}
        onLoad={() => {
          setMapReady(true)
        }}
        onClick={handleClick}
        onMoveEnd={reportViewport}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(undefined)}
      >
        <NavigationControl position="bottom-right" showCompass={false} />
        <GeofenceLayers geofences={geofences} highlightIds={highlightIds} />
        {hover && (
          <Popup
            longitude={hover.longitude}
            latitude={hover.latitude}
            closeButton={false}
            closeOnClick={false}
            offset={12}
            className="pointer-events-none"
          >
            <span className="text-xs font-semibold text-neutral-800">{hover.name}</span>
          </Popup>
        )}
      </MapboxMap>
      <MapStyleToggles
        style={style}
        show3d={false}
        onChange={(next) => {
          setMapReady(false)
          setStyle(next)
        }}
      />
    </div>
  )
}
